import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

export default function InterestRates() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", annual_rate: "0" });

  const load = () => api.get("/interest-rates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      (it.code || "").toLowerCase().includes(s) ||
      (it.name || "").toLowerCase().includes(s) ||
      String(it.annual_rate).includes(s)
    );
  }, [items, q]);

  const openNew = () => { setEditing(null); setForm({ code: "", name: "", annual_rate: "0" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ code: it.code, name: it.name, annual_rate: String(it.annual_rate) }); setOpen(true); };

  const submit = async () => {
    try {
      const payload = { code: form.code, name: form.name, annual_rate: parseFloat(form.annual_rate) };
      if (editing) await api.put(`/interest-rates/${editing.id}`, payload);
      else await api.post("/interest-rates", payload);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus bunga?")) return;
    await api.delete(`/interest-rates/${id}`); toast.success("Terhapus"); load();
  };

  return (
    <div>
      <PageHeader title="Master Bunga Kredit" subtitle="Kode bunga per tahun untuk digunakan saat transaksi kredit"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-rate-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah Bunga
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} Bunga</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div><Label className="text-xs uppercase tracking-wider">Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="R18" className="mt-2 rounded-none font-mono-num" data-testid="rate-code" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="18% per tahun" className="mt-2 rounded-none" data-testid="rate-name" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Rate per tahun (%)</Label><Input type="number" step="0.01" value={form.annual_rate} onChange={(e) => setForm({ ...form, annual_rate: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="rate-value" /></div>
                <Button onClick={submit} data-testid="rate-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <SearchBar value={q} onChange={setQ} placeholder="Cari kode / nama bunga..." testid="rates-search" count={filtered.length} />
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Kode</th><th className="p-4">Nama</th><th className="p-4 text-right">Rate (%/thn)</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-gray-100">
                  <td className="p-4 font-mono-num font-semibold">{it.code}</td>
                  <td className="p-4">{it.name}</td>
                  <td className="p-4 text-right font-mono-num">{it.annual_rate}%</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(it)} className="mr-3 text-gray-500 hover:text-black"><PencilSimple size={16} /></button>
                    <button onClick={() => del(it.id)} className="text-gray-500 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="4" className="p-10 text-center text-sm text-gray-500">{items.length === 0 ? "Belum ada bunga" : "Tidak ada hasil"}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
